import React, { useCallback, useMemo } from 'react'
import { DatePickerValue, DateRangeValue, VariableDatePickerValue } from '@/components/date/date.types'
import dayjs from 'dayjs'
import DatePicker, { DatePickerProps, registerLocale } from 'react-datepicker'
import { ko } from 'date-fns/locale/ko'
import { DateRangePickerWrap } from '@/components/date/date_styles'
import { dateDefaultOptions } from '@/components/date/date_options'
import 'react-datepicker/dist/react-datepicker.css'

registerLocale('ko', ko)

interface Props extends Omit<DatePickerProps, 'value' | 'onChange' | 'onSelect' | 'excludeScrollbar'> {
  value?: DateRangeValue
  maxDateRange?: number
  onChange?: (v: DateRangeValue) => void
}

function DateRangePickerInput({ dateFormat, showMonthYearPicker, value, maxDateRange, onChange }: Props) {
  const getValidDate = (v: VariableDatePickerValue): DatePickerValue => (dayjs(v).isValid() && v ? dayjs(v).toDate() : null)

  const [start, end] = value ?? []
  const startDate = getValidDate(start) ?? undefined
  const endDate = getValidDate(end) ?? undefined

  const maxEndDate = useMemo(
    () => (startDate && maxDateRange ? dayjs(startDate).add(maxDateRange, 'day').toDate() : undefined),
    [startDate, maxDateRange],
  )

  const handleStartDateChange = useCallback(
    (date: Date | null) => {
      if (maxDateRange) {
        onChange?.([date, null])
      } else {
        onChange?.([date, endDate])
      }
    },
    [onChange],
  )

  return (
    <DateRangePickerWrap>
      <DatePicker
        {...dateDefaultOptions}
        dateFormat={dateFormat ?? dateDefaultOptions.dateFormat}
        yearClassName={(date) => `${date}년`}
        selectsStart
        selected={startDate}
        onChange={handleStartDateChange}
        onSelect={handleStartDateChange}
        startDate={startDate}
        endDate={endDate}
        showMonthYearPicker={showMonthYearPicker}
      />
      -
      <DatePicker
        {...dateDefaultOptions}
        dateFormat={dateFormat ?? dateDefaultOptions.dateFormat}
        selectsEnd
        selected={endDate}
        onChange={(date) => onChange?.([startDate, date])}
        onSelect={(date) => onChange?.([startDate, date])}
        startDate={startDate}
        endDate={endDate}
        minDate={startDate}
        maxDate={maxEndDate}
        showMonthYearPicker={showMonthYearPicker}
      />
    </DateRangePickerWrap>
  )
}

export default DateRangePickerInput
